import { PageHeader, LoadingState, DataTable, FilterBar, EmptyState } from '../components/AdminPageUI';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, MenuItem, Avatar, Stack,
  FormControl, InputLabel, Select, CircularProgress, Alert,
  Chip, Pagination, Table, TableBody, TableCell, TableHead, TableRow,
  Container, useMediaQuery, Card, CardContent
} from '@mui/material';
import { Book, CalendarToday, Class, FilterList, School, Schedule } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const ExercisesPage = ({ schoolId: propSchoolId }) => {


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State for fallback schoolId if prop is missing
  const [fallbackSchoolId, setFallbackSchoolId] = useState(null);
  const schoolId = propSchoolId || fallbackSchoolId;

  // Form state
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [term, setTerm] = useState('');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [week, setWeek] = useState('');
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [allTerms, setAllTerms] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    finalized: undefined
  });

  // Temporary fallback for schoolId (remove after ensuring prop is passed)
  useEffect(() => {
    if (!propSchoolId) {
      // console.warn('schoolId prop not provided - checking local storage');
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData?.school) {
        // console.log('Using schoolId from local storage:', userData.school);
        setFallbackSchoolId(userData.school);
      }
    }
  }, [propSchoolId]);

  // 1. Fetch academic years
  const fetchAcademicYears = useCallback(async () => {
    try {
      if (!schoolId) {
        return;
      }

      const res = await api.get('/term/years', {
        params: { school: schoolId }
      });

      const years = res.data?.academicYears || [];

      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
      } else {
        setAvailableAcademicYears([]);
        setAcademicYear('');
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Failed to load academic years');
    }
  }, [schoolId]);

  // 2. Fetch terms for selected academic year
  const fetchTerms = useCallback(async () => {
    try {
      if (!schoolId) {
        setAvailableTerms([]);
        setTerm('');
        setSelectedTermId('');
        return;
      }

      const res = await api.get('/term/all', {
        params: { school: schoolId }
      });

      const termsData = res.data?.allTerms || [];
      setAllTerms(termsData);

      const termsForYear = academicYear
        ? termsData.filter(t => t.academicYear === academicYear)
        : termsData;

      if (termsForYear.length > 0) {
        const uniqueTerms = [...new Set(termsForYear.map(t => t.term))].sort();
        setAvailableTerms(uniqueTerms);

        if (term) {
          const match = termsForYear.find(t => t.term === term);
          if (match) {
            setSelectedTermId(match._id);
          } else {
            setTerm('');
            setSelectedTermId('');
          }
        } else {
          setSelectedTermId('');
        }
      } else {
        setAvailableTerms([]);
        setTerm('');
        setSelectedTermId('');
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Failed to load terms');
    }
  }, [schoolId, academicYear, term]);

  // 3. Fetch weeks for selected term
  const fetchTermWeeks = useCallback(async () => {
    try {
      if (!academicYear || !term || !schoolId) {
        setAvailableWeeks([]);
        setWeek('');
        return;
      }

      setLoadingWeeks(true);

      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear,
          term
        }
      });

      if (!res.data?.weeks) {
        throw new Error('Invalid weeks data format');
      }

      const weeksData = res.data.weeks;
      const sortedWeeks = [...weeksData].sort((a, b) => a.weekNumber - b.weekNumber);
      const weeks = sortedWeeks.map(w => w.label);

      setAvailableWeeks(weeks);

      const currentActiveWeek = sortedWeeks.find(w => w.isCurrent);
      if (weeks.length > 0 && !week && currentActiveWeek) {
        setWeek(currentActiveWeek.label);
      } else if (weeks.length > 0 && week && !weeks.includes(week)) {
        setWeek(currentActiveWeek ? currentActiveWeek.label : '');
      }
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      setAvailableWeeks([]);
      setWeek('');
      toast.error('Failed to load weeks');
    } finally {
      setLoadingWeeks(false);
    }
  }, [schoolId, academicYear, term, week]);

  // 4. Fetch classes
  const fetchClasses = useCallback(async () => {
    // console.groupCollapsed('[ExercisesPage] fetchClasses');
    try {
      if (!schoolId) {
        // console.log('Skipping - schoolId not available');
        return;
      }

      // console.log('Fetching classes for school:', schoolId);
      const res = await api.get('/classes', {
        params: { school: schoolId }
      });

      const classList = res.data.classes || [];
      setClasses(classList);
      setSelectedClassId(classList.length > 0 ? classList[0]._id : '');
      // console.log(`Loaded ${classList.length} classes`);
    } catch (err) {
      console.error('Failed to load classes:', err);
      toast.error('Failed to load classes');
    } finally {
      // console.groupEnd();
    }
  }, [schoolId]);

  // 5. Fetch exercises with teacher and subject data
  const fetchExercises = useCallback(async () => {
    // console.groupCollapsed('[ExercisesPage] fetchExercises');
    try {
      if (!selectedClassId || !week || !selectedTermId) {
        // console.log('Skipping - missing required parameters');
        setExercises([]);
        return;
      }

      setLoading(true);
      // console.log(`Fetching exercises for class: ${selectedClassId}, week: ${week}`);

      const weekNumber = parseInt(week.replace('Week ', ''));

      const res = await api.get('/exercises/summary', {
        params: {
          classId: selectedClassId,
          termId: selectedTermId,
          week: weekNumber,
          page: pagination.page,
          limit: pagination.limit,
          finalized: filters.finalized,
          populate: 'teacher,class,term'
        }
      });

      // Transform data with proper date handling
      const formattedExercises = res.data?.data?.map(exercise => {
        const lastUpdatedDate = exercise.updatedAt || exercise.createdAt;
        const isValidDate = lastUpdatedDate && !isNaN(new Date(lastUpdatedDate).getTime());

        return {
          id: exercise._id,
          teacher: {
            name: exercise.teacher?.user?.name || exercise.teacher?.name || 'Unknown Teacher',
            subjects: exercise.teacher?.subjects?.map(s => s.name) || []
          },
          subjectCounts: exercise.subjectCounts || [],
          class: {
            name: exercise.class?.name || 'Unknown Class',
            displayName: exercise.class?.displayName || exercise.class?.name || 'Unknown Class',
            level: exercise.class?.level || ''
          },
          totalExercises: exercise.totalExercises,
          week: exercise.week,
          term: exercise.term?.term || 'Unknown Term',
          academicYear: exercise.term?.academicYear || '',
          finalized: exercise.finalized || false,
          updatedAt: exercise.updatedAt,
          createdAt: exercise.createdAt,
          lastUpdated: isValidDate ? lastUpdatedDate : null
        };
      }) || [];

      setExercises(formattedExercises);
      setPagination(prev => ({
        ...prev,
        total: res.data?.meta?.totalItems || res.data?.total || 0
      }));

    } catch (err) {
      console.error('Failed to fetch exercises:', err);
      toast.error(err.response?.data?.message || 'Unable to load exercises');
      setExercises([]);
    } finally {
      setLoading(false);
      // console.groupEnd();
    }
  }, [selectedClassId, week, selectedTermId, pagination.page, pagination.limit, filters.finalized]);

  // Sync term ID when term name changes
  useEffect(() => {
    if (term && academicYear && allTerms.length > 0) {
      const termObj = allTerms.find(
        t => t.term === term && t.academicYear === academicYear
      );
      if (termObj) {
        setSelectedTermId(termObj._id);
        // console.log('Updated selectedTermId to:', termObj._id);
      }
    }
  }, [term, academicYear, allTerms]);

  // Initialize data when schoolId becomes available
  useEffect(() => {
    if (schoolId) {
      // Aggressively pre-fill current term and week using the global timeline endpoint
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          const cw = res.data;
          if (userSelectedAcademicYearRef.current) return;
          if (cw?.academicYear && cw?.termName && cw?.termId) {
            setAcademicYear(cw.academicYear);
            setTerm(cw.termName);
            setSelectedTermId(cw.termId);
            setWeek(cw.current > 0 ? `Week ${cw.current}` : '');
          }
        }).catch(err => console.error('Auto-select error:', err));

      setFetching(true);
      Promise.all([fetchAcademicYears(), fetchClasses()])
        .finally(() => setFetching(false));
    }
  }, [schoolId, fetchAcademicYears, fetchClasses]);

  // Fetch terms when academic year changes
  useEffect(() => {
    if (schoolId && academicYear) {
      fetchTerms();
    }
  }, [schoolId, academicYear, fetchTerms]);

  // Fetch weeks when term changes
  useEffect(() => {
    if (schoolId && term && academicYear) {
      fetchTermWeeks();
    }
  }, [schoolId, term, academicYear, fetchTermWeeks]);

  // Fetch exercises when parameters change
  useEffect(() => {
    if (schoolId) {
      fetchExercises();
    }
  }, [schoolId, selectedClassId, week, selectedTermId, pagination.page, pagination.limit, filters.finalized, fetchExercises]);

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'T';

  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  // Mobile Exercise Card Component
  const MobileExerciseCard = ({ exercise }) => (
    <Card sx={{ mb: 2, boxShadow: 2, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Teacher Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  bgcolor: stringToColor(exercise.teacher.name),
                  width: 40,
                  height: 40
                }}
              >
                {getInitials(exercise.teacher.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {exercise.teacher.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {exercise.teacher.subjects.join(', ')}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={exercise.finalized ? 'Finalized' : 'Draft'}
              size="small"
              color={exercise.finalized ? 'success' : 'warning'}
              variant={exercise.finalized ? 'filled' : 'outlined'}
            />
          </Stack>

          {/* Class and Exercises */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                CLASS
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {exercise.class.displayName || exercise.class.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                EXERCISES
              </Typography>
              <Chip
                label={exercise.totalExercises}
                color="secondary"
                variant="filled"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Stack>

          {/* Week and Term */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                WEEK
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="500">
                  Week {exercise.week}
                </Typography>
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                TERM
              </Typography>
              <Typography variant="body2">
                {exercise.term}
              </Typography>
            </Box>
          </Stack>

          {/* Subjects */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              SUBJECTS
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {exercise.subjectCounts && exercise.subjectCounts.length > 0 ? (
                 exercise.subjectCounts.map((sc, i) => (
                    <Chip
                      key={i}
                      label={`${sc.subject?.name || sc.subject?.shortName || 'Unknown'}: ${sc.count}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                 ))
              ) : (
                  exercise.teacher.subjects.map((subj, i) => (
                    <Chip
                      key={i}
                      label={subj}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (!schoolId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Weekly Exercises</>} description={<>Manage and view exercise submissions by teachers</>}></PageHeader>

      {fetching ? (
        <LoadingState />
      ) : (
        <>
          {/* Filter Section */}
          <FilterBar className="workspace-section" sx={{
            p: isMobile ? 2 : 3,
            mb: isMobile ? 2 : 3,
            boxShadow: isMobile ? 1 : 3
          }}>
            <Typography variant={isMobile ? "h6" : "h6"} gutterBottom>
              Filter Exercises
            </Typography>

            <Grid container spacing={isMobile ? 2 : 3}>
              {/* Academic Year Dropdown */}
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Academic Year</InputLabel>
                  <Select
                    value={academicYear}
                    onChange={(e) => {
                      userSelectedAcademicYearRef.current = true;
                      setAcademicYear(e.target.value);
                    }}
                    label="Academic Year"
                    disabled={availableAcademicYears.length === 0}
                  >
                    {availableAcademicYears.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Term Dropdown */}
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Term</InputLabel>
                  <Select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    label="Term"
                    disabled={availableTerms.length === 0}
                  >
                    {availableTerms.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Week Dropdown */}
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Week</InputLabel>
                  <Select
                    value={week}
                    onChange={(e) => setWeek(e.target.value)}
                    label="Week"
                    disabled={loadingWeeks || availableWeeks.length === 0}
                  >
                    {loadingWeeks ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : availableWeeks.length > 0 ? (
                      availableWeeks.map(w => (
                        <MenuItem key={w} value={w}>{w}</MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No weeks available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Class Dropdown */}
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={selectedClassId}
                    onChange={(e) => setSelectedClassId(e.target.value)}
                    label="Class"
                    disabled={classes.length === 0}
                  >
                    {classes.map(cls => (
                      <MenuItem key={cls._id} value={cls._id}>
                        {cls.displayName || cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter Controls */}
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={1}
                  alignItems={isMobile ? "flex-start" : "center"}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Status:
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      label="All"
                      clickable
                      color={filters.finalized === undefined ? 'primary' : 'default'}
                      onClick={() => handleFilterChange('finalized', undefined)}
                      size={isMobile ? "small" : "medium"}
                    />
                    <Chip
                      label="Finalized"
                      clickable
                      color={filters.finalized === true ? 'primary' : 'default'}
                      onClick={() => handleFilterChange('finalized', true)}
                      size={isMobile ? "small" : "medium"}
                    />
                    <Chip
                      label="Drafts"
                      clickable
                      color={filters.finalized === false ? 'primary' : 'default'}
                      onClick={() => handleFilterChange('finalized', false)}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </FilterBar>

          {/* Exercises List */}
          <Paper className="workspace-section" sx={{ boxShadow: isMobile ? 1 : 3 }}>
            <Box sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Exercise Summary ({exercises.length} of {pagination.total})
              </Typography>
            </Box>

            {loading ? (
              <LoadingState />
            ) : exercises.length > 0 ? (
              <>
                {isMobile ? (
                  // Mobile View - Cards
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      {exercises.map((exercise) => (
                        <MobileExerciseCard key={exercise.id} exercise={exercise} />
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  // Desktop/Tablet View - Table
                  <Box sx={{
                    width: '100%',
                    overflow: 'auto',
                    maxHeight: isTablet ? '60vh' : 'none'
                  }}>
                    <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                      <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHER</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>SUBJECT</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>EXERCISES</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>WEEK</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STATUS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
{exercises.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                        {exercises.map((ex) => (
                          <TableRow
                            key={ex.id}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar
                                  sx={{
                                    bgcolor: stringToColor(ex.teacher.name),
                                    width: 40,
                                    height: 40
                                  }}
                                >
                                  {getInitials(ex.teacher.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="500">
                                    {ex.teacher.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {ex.teacher.subjects.join(', ')}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                {ex.subjectCounts && ex.subjectCounts.length > 0 ? (
                                    ex.subjectCounts.map((sc, i) => (
                                        <Chip
                                            key={i}
                                            label={`${sc.subject?.name || sc.subject?.shortName || 'Unknown'}: ${sc.count}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ mb: 0.5 }}
                                        />
                                    ))
                                ) : (
                                  ex.teacher.subjects.map((subj, i) => (
                                    <Chip
                                      key={i}
                                      label={subj}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ mb: 0.5 }}
                                    />
                                  ))
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body1" fontWeight="500">
                                {ex.class.displayName || ex.class.name}
                              </Typography>

                              {ex.class.level && (
                                <Typography variant="body2" color="text.secondary">
                                  Level {ex.class.level}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={ex.totalExercises}
                                color="secondary"
                                variant="filled"
                                sx={{ fontWeight: 'bold', minWidth: 60 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <CalendarToday fontSize="small" color="action" />
                                <Typography variant="body1" fontWeight="500">
                                  Week {ex.week}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={ex.finalized ? 'Finalized' : 'Draft'}
                                size="small"
                                color={ex.finalized ? 'success' : 'warning'}
                                variant={ex.finalized ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table></DataTable>
                  </Box>
                )}

                {/* Pagination */}
                <Box sx={{
                  p: isMobile ? 2 : 3,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={isMobile ? 2 : 0}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Showing {exercises.length} of {pagination.total} entries
                    </Typography>
                    <Pagination
                      count={Math.ceil(pagination.total / pagination.limit)}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size={isMobile ? "small" : "medium"}
                    />
                  </Stack>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <Book sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  mb: 2,
                  opacity: 0.5
                }} />
                <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
                  No exercises found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {week && term ? `No exercises found for ${week} of ${term} ${academicYear}` : 'Select filters to view exercises'}
                </Typography>
                <Chip
                  label="Clear Filters"
                  clickable
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    setFilters({ finalized: undefined });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default ExercisesPage;
